<script setup>
  import { ref, watch, computed } from "vue";

  import { FormDialog, FormField } from "@/components/episto-ui/form-dialog";
  import { Input } from "@/components/ui/input";

  import { useContractsStore } from "@stores/contracts";

  const props = defineProps({
    open: { type: Boolean, default: false },
    survey: { type: Object, default: null },
  });

  const emit = defineEmits(["update:open", "saved"]);

  const contractsStore = useContractsStore();

  const value = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const canSave = computed(() => value.value === null || value.value === "" || value.value >= 0);

  watch(
    () => [props.open, props.survey],
    ([open, survey]) => {
      if (!open) return;
      value.value = survey?.credits_consumed_manual ?? null;
      error.value = null;
    },
    { immediate: true },
  );

  const save = async () => {
    if (!props.survey) return;
    loading.value = true;
    error.value = null;
    try {
      const persistValue = value.value === "" ? null : value.value;
      await contractsStore.setManualCredits(props.survey.token, persistValue);
      emit("saved");
      emit("update:open", false);
    } catch (e) {
      error.value = e?.message || "An error occurred while updating manual credits.";
    } finally {
      loading.value = false;
    }
  };
</script>

<template>
  <FormDialog
    :open="open"
    is-editing
    entity-name="Manual Credits"
    :loading="loading"
    :can-save="canSave"
    max-width="450px"
    @update:open="emit('update:open', $event)"
    @save="save">
    <FormField
      id="manual-survey"
      label="Survey"
      readonly>
      {{ survey?.name }}
    </FormField>

    <FormField
      id="manual-credits"
      label="Manual credit count">
      <Input
        id="manual-credits"
        v-model.number="value"
        type="number"
        min="0"
        placeholder="Leave empty to revert to auto" />
    </FormField>

    <p
      v-if="error"
      class="text-sm text-status-error-strong">
      {{ error }}
    </p>
  </FormDialog>
</template>
